import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { baseParamState, PlaybookFilter } from '../../shared/constants.js';
import { authActions } from '../auth/index.js';
import { playEditorActions } from '../play-editor/index.js';

const createParamUpdater =
    <K extends keyof RouterState>(stateKey: K) =>
    (state: RouterState, action: PayloadAction<RouterState[K]>) => {
        const existingValue = state[stateKey];

        if (existingValue === action.payload) {
            return;
        }

        state[stateKey] = action.payload;
    };

export type RouterState = {
    deeplink: string;
    communityPageNumber: number;
    communityPlayId: string;
    communityProfileId: string;
    communitySearchText: string;
    publicLinkId: string;
    invoiceLinkId: string;
    invoiceError: string;
    emailAction: {
        mode: string;
        oobCode: string;
        apiKey: string;
        continueUrl: string;
    };

    activeTeam: string;
    playId: string;
    folderId: string;
    viewerDetailsPlayId: string;
    rosterPlayerId: string;
    editBillingEntityId: string;
    viewDocumentAcksId: string;
    viewPostId: string;
    selectedEvaluationTemplateId: string;
    joinTeamCode: string;
    joinTeamId: string;
    shownScheduleTab: string;
    viewEventId: string;
    viewPracticeTemplateId: string;
    rsvpUpdateStatus: string;
    playerEvaluationId: string;
    playbookFilter: PlaybookFilter;
    creatingPlay: boolean;
    playbookBrowseTab: string;
    selectedFormationId: string;
    selectedConceptId: string;
};

const routerDefaultState: RouterState = baseParamState;

const { reducer: routerReducer, actions: routerActions } = createSlice({
    name: 'router',
    initialState: routerDefaultState,
    reducers: {
        SetActiveTeam: createParamUpdater('activeTeam'),
        SetDeeplink: createParamUpdater('deeplink'),
        OpenPlay: (state, action: PayloadAction<string>) => {
            const existingValue = state.playId;

            if (existingValue === action.payload) {
                return;
            }

            state.playId = action.payload;

            if (action.payload) {
                state.selectedFormationId = '';
                state.selectedConceptId = '';
            }
        },
        OpenPage: createParamUpdater('communityPageNumber'),
        OpenCommunityPlay: createParamUpdater('communityPlayId'),
        SetCommunityProfileId: createParamUpdater('communityProfileId'),
        SetCommunitySearch: createParamUpdater('communitySearchText'),
        OpenPlayFolder: createParamUpdater('folderId'),
        ShowViewerDetails: createParamUpdater('viewerDetailsPlayId'),
        SetRosterPlayer: createParamUpdater('rosterPlayerId'),
        SetEditBillingEntity: createParamUpdater('editBillingEntityId'),
        ViewDocumentAcks: createParamUpdater('viewDocumentAcksId'),
        ViewPost: createParamUpdater('viewPostId'),
        SetViewEvent: createParamUpdater('viewEventId'),
        SetViewPracticeTemplate: createParamUpdater('viewPracticeTemplateId'),
        SetScheduleShownTab: createParamUpdater('shownScheduleTab'),
        SetSelectedEvaluationTemplate: createParamUpdater('selectedEvaluationTemplateId'),
        SetPlayerEvaluationId: createParamUpdater('playerEvaluationId'),
        SetPlaybookFilter: createParamUpdater('playbookFilter'),
        SetCreatingPlay: createParamUpdater('creatingPlay'),
        SetPlaybookBrowseTab: createParamUpdater('playbookBrowseTab'),
        SelectFormation: (state, action: PayloadAction<string>) => {
            state.selectedFormationId = action.payload;

            if (action.payload) {
                state.selectedConceptId = '';
                state.playId = '';
            }
        },
        SelectConcept: (state, action: PayloadAction<string>) => {
            state.selectedConceptId = action.payload;

            if (action.payload) {
                state.selectedFormationId = '';
                state.playId = '';
            }
        },

        UpdateRouterState: (state, action: PayloadAction<RouterState>) => {
            return action.payload;
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(playEditorActions.CreatePlay, (state, action) => {
                state.playId = action.payload.id;
                state.selectedFormationId = '';
                state.selectedConceptId = '';
            })
            .addCase(playEditorActions.NewPlaySaved, (state, action) => {
                state.playId = action.payload;
                state.selectedFormationId = '';
                state.selectedConceptId = '';
            })
            .addCase(authActions.PurgeProfile, (state) => ({
                ...routerDefaultState,
                deeplink: state.deeplink,
                communityPageNumber: state.communityPageNumber,
                communityPlayId: state.communityPlayId,
                communityProfileId: state.communityProfileId,
                communitySearchText: state.communitySearchText,
                publicLinkId: state.publicLinkId,
                invoiceLinkId: state.invoiceLinkId,
                invoiceError: state.invoiceError,
                emailAction: state.emailAction,
            })),
});

export { routerReducer, routerActions, routerDefaultState };
